import { Trash2, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import LoadingSpinner from '../../../../components/LoadingSpinner';

import {
  deleteOutreachByBatch,
  deleteOutreachPersons,
  fetchImportBatches,
  type DeleteProgress,
  type ImportBatchRow,
} from './outreachImportService';

export type DeleteMode = 'selected' | 'batch' | 'all';

interface OutreachDeleteModalProps {
  mode: DeleteMode;
  selectedIds?: string[];
  totalCount?: number;
  onClose: () => void;
  onDeleted: (deleted: number, cancelled: boolean) => void;
  onWipeAll: () => Promise<{ ok: boolean; error?: string }>;
}

function modeTitle(mode: DeleteMode, count: number): string {
  switch (mode) {
    case 'selected': return `Delete ${count.toLocaleString()} selected record(s)`;
    case 'batch': return 'Delete by import batch';
    case 'all': return `Delete all ${count.toLocaleString()} records`;
    default: return 'Delete records';
  }
}

export function OutreachDeleteModal({
  mode,
  selectedIds = [],
  totalCount = 0,
  onClose,
  onDeleted,
  onWipeAll,
}: OutreachDeleteModalProps) {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<DeleteProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<ImportBatchRow[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<string>('');
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (mode === 'batch') {
      void fetchImportBatches().then(setBatches);
    }
  }, [mode]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !running) onClose(); };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', h);
      document.body.style.overflow = '';
    };
  }, [onClose, running]);

  const count = mode === 'selected' ? selectedIds.length : totalCount;
  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
    : 0;

  const handleCancel = () => abortRef.current?.abort();

  const runDelete = async () => {
    setRunning(true);
    setError(null);
    setProgress(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      if (mode === 'all') {
        setProgress({ phase: 'deleting', processed: 0, total: count, deleted: 0 });
        const result = await onWipeAll();
        if (!result.ok) {
          setError(result.error ?? 'Delete all failed.');
          return;
        }
        setProgress({ phase: 'done', processed: count, total: count, deleted: count });
        onDeleted(count, false);
        return;
      }

      if (mode === 'batch') {
        if (!selectedBatch) {
          setError('Select an import batch.');
          return;
        }
        setProgress({ phase: 'deleting', processed: 0, total: 1, deleted: 0 });
        const result = await deleteOutreachByBatch(selectedBatch);
        if (!result.ok) {
          setError(result.error ?? 'Batch delete failed.');
          return;
        }
        const deleted = result.deleted ?? 0;
        setProgress({ phase: 'done', processed: 1, total: 1, deleted });
        onDeleted(deleted, false);
        return;
      }

      const result = await deleteOutreachPersons(selectedIds, {
        signal: controller.signal,
        onProgress: setProgress,
      });
      if (result.error) {
        setError(result.error);
        return;
      }
      onDeleted(result.deleted, result.cancelled);
    } finally {
      setRunning(false);
      abortRef.current = null;
    }
  };

  const confirmDelete = () => {
    const msg = mode === 'batch'
      ? 'Delete all persons from this import batch? This cannot be undone.'
      : `Delete ${count.toLocaleString()} record(s)? Already-deleted rows are kept if you cancel mid-way.`;
    if (!window.confirm(msg)) return;
    void runDelete();
  };

  const done = progress?.phase === 'done';

  return createPortal(
    <>
      <div aria-hidden className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]" onClick={running ? undefined : onClose} />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div
          className="w-full sm:max-w-[480px] max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl flex flex-col"
          style={{
            background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))',
            border: '1px solid var(--vscode-border-primary)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-600" />
              <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                {modeTitle(mode, count)}
              </h2>
            </div>
            {!running && (
              <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70 min-h-[44px] min-w-[44px] flex items-center justify-center">
                <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
              </button>
            )}
          </div>

          <div className="p-5 space-y-4">
            {mode === 'batch' && !running && !done && (
              <div>
                <label className="text-[12px] font-medium block mb-1.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                  Import batch
                </label>
                <select
                  value={selectedBatch}
                  onChange={e => setSelectedBatch(e.target.value)}
                  className="w-full rounded-lg px-3 py-2.5 text-[13px] min-h-[44px]"
                  style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-primary)' }}
                >
                  <option value="">Select batch…</option>
                  {batches.map(b => (
                    <option key={b.id} value={b.id}>
                      {b.filename} · {b.sheet_name ?? 'sheet'} · {b.imported_count}/{b.total_rows} · {new Date(b.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {running && progress && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <LoadingSpinner size="sm" showText={false} />
                  <p className="text-[13px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                    Deleting… {progress.deleted.toLocaleString()} removed
                  </p>
                </div>
                {mode === 'selected' && (
                  <>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--vscode-border-secondary)' }}>
                      <div className="h-full rounded-full transition-all duration-300 bg-red-600" style={{ width: `${pct}%` }} />
                    </div>
                    <p className="text-[12px] tabular-nums" style={{ color: 'var(--vscode-text-secondary)' }}>
                      {progress.processed.toLocaleString()} / {progress.total.toLocaleString()} processed
                    </p>
                  </>
                )}
                {mode === 'selected' && (
                  <>
                    <p className="text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                      Records already deleted are kept if you cancel.
                    </p>
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="w-full py-2.5 rounded-xl text-[13px] font-medium min-h-[44px]"
                      style={{ border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
                    >
                      Cancel delete
                    </button>
                  </>
                )}
              </div>
            )}

            {error && (
              <p className="text-[12px] px-3 py-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c' }}>
                {error}
              </p>
            )}

            {done && progress && (
              <div className="rounded-xl p-4 text-[13px]" style={{ background: 'rgba(16,185,129,0.08)', color: '#065f46' }}>
                Deleted {progress.deleted.toLocaleString()} record(s).
              </div>
            )}

            {!running && !done && (
              <div className="flex gap-2">
                <button type="button" onClick={onClose}
                  className="flex-1 py-2.5 rounded-xl text-[13px] font-medium min-h-[44px]"
                  style={{ border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}>
                  Cancel
                </button>
                <button type="button" onClick={confirmDelete}
                  className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold min-h-[44px] text-white bg-red-600">
                  Delete
                </button>
              </div>
            )}

            {done && (
              <button type="button" onClick={onClose}
                className="w-full py-2.5 rounded-xl text-[13px] font-medium min-h-[44px]"
                style={{ background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-primary)' }}>
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default OutreachDeleteModal;
